import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Navigation, AlertTriangle, WifiOff, Gauge, History as HistoryIcon, Radio } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Admin control-center for the fleet, fed by the driver app's pings (proxied
// from the mobile backend). Plain Leaflet + free OpenStreetMap tiles.
//   • LIVE tab: every bus's latest position, polled every POLL_MS. A bus is
//     "offline" if its last ping is older than STALE_SECONDS; "speeding" if its
//     latest speed exceeds the (adjustable) threshold.
//   • HISTORY tab: pick a past route session → draw the bus's actual trajectory
//     (polyline, red where it was speeding) with the session's stats.
const JOUNIEH = [33.9808, 35.6178];
const POLL_MS = 10000;
const STALE_SECONDS = 90;

const statusOf = (b, overspeed) => {
  if (!b.isActive || b.secondsAgo > STALE_SECONDS) return 'offline';
  if (b.speedKmh != null && b.speedKmh > overspeed) return 'speeding';
  return 'active';
};
const COLORS = { active: '#16a34a', speeding: '#dc2626', offline: '#9ca3af' };

const agoText = (s) => {
  if (s == null) return '—';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
};
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString() : '—');
const durationText = (a, b) => {
  if (!a) return '—';
  const ms = (b ? new Date(b) : new Date()) - new Date(a);
  const m = Math.max(0, Math.round(ms / 60000));
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`;
};
const dotIcon = (bg, glyph) => L.divIcon({
  className: '',
  html: `<div style="background:${bg};color:#fff;border-radius:9999px;width:22px;height:22px;display:flex;align-items:center;justify-content:center;font-size:12px;box-shadow:0 1px 3px rgba(0,0,0,.5);border:2px solid #fff;transform:translate(-50%,-50%)">${glyph}</div>`,
  iconSize: [0, 0],
});

const BusLiveMapModal = ({ isOpen, onClose, apiCall, showError }) => {
  const [tab, setTab] = useState('live');
  const [overspeed, setOverspeed] = useState(80);

  // Live
  const [loading, setLoading] = useState(true);
  const [buses, setBuses] = useState([]);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  // History
  const [days, setDays] = useState(7);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sessions, setSessions] = useState([]);
  const [summary, setSummary] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [busFilter, setBusFilter] = useState('all');
  const [selected, setSelected] = useState(null);   // selected session
  const [routePoints, setRoutePoints] = useState([]);
  const [routeLoading, setRouteLoading] = useState(false);

  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const containerRef = useRef(null);
  const fittedRef = useRef(false);

  // ---- Live polling (only on the Live tab) ----
  useEffect(() => {
    if (!isOpen || tab !== 'live') return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const data = await apiCall('/mobile/bus-live');
        if (cancelled) return;
        setBuses(Array.isArray(data?.buses) ? data.buses : []);
        setLastRefreshed(new Date());
      } catch (error) {
        if (!cancelled) showError('Failed to load live bus map: ' + error.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const timer = setInterval(load, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tab]);

  // ---- History: load sessions when entering the tab ----
  useEffect(() => {
    if (!isOpen || tab !== 'history') return undefined;
    let cancelled = false;
    (async () => {
      try {
        setSessionsLoading(true);
        // Explicit from/to override the rolling `days` window.
        const qs = (from || to)
          ? [from && `from=${from}`, to && `to=${to}`].filter(Boolean).join('&')
          : `days=${days}`;
        const data = await apiCall(`/mobile/bus-analytics?${qs}`);
        if (!cancelled) {
          setSessions(Array.isArray(data?.sessions) ? data.sessions : []);
          setSummary(Array.isArray(data?.summary) ? data.summary : []);
        }
      } catch (error) {
        if (!cancelled) showError('Failed to load route history: ' + error.message);
      } finally {
        if (!cancelled) setSessionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tab, days, from, to]);

  // ---- History: load points when a session is picked ----
  useEffect(() => {
    if (!selected) { setRoutePoints([]); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        setRouteLoading(true);
        const data = await apiCall(`/mobile/bus-route-points/${selected.id}`);
        if (!cancelled) setRoutePoints(Array.isArray(data?.points) ? data.points : []);
      } catch (error) {
        if (!cancelled) showError('Failed to load trajectory: ' + error.message);
      } finally {
        if (!cancelled) setRouteLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Reset on open/close.
  useEffect(() => {
    if (isOpen) { fittedRef.current = false; setLoading(true); setTab('live'); }
    else { setBuses([]); setSessions([]); setSummary([]); setSelected(null); setRoutePoints([]); setFrom(''); setTo(''); }
  }, [isOpen]);

  // Clear the drawing layer when switching tabs / selection.
  useEffect(() => {
    if (layerRef.current) layerRef.current.clearLayers();
    fittedRef.current = false;
  }, [tab]);

  const liveRows = useMemo(() => {
    const rank = { speeding: 0, active: 1, offline: 2 };
    return [...buses].sort((a, b) =>
      rank[statusOf(a, overspeed)] - rank[statusOf(b, overspeed)] || a.busId - b.busId);
  }, [buses, overspeed]);

  const counts = useMemo(() => {
    const c = { active: 0, speeding: 0, offline: 0 };
    buses.forEach((b) => { c[statusOf(b, overspeed)]++; });
    return c;
  }, [buses, overspeed]);

  const busOptions = useMemo(
    () => Array.from(new Set(sessions.map((s) => s.bus_id))).sort((a, b) => a - b),
    [sessions]
  );
  const sessionRows = useMemo(
    () => sessions.filter((s) => busFilter === 'all' || String(s.bus_id) === String(busFilter)),
    [sessions, busFilter]
  );
  const summaryRows = useMemo(
    () => summary.filter((r) => busFilter === 'all' || String(r.bus_id) === String(busFilter)),
    [summary, busFilter]
  );
  const totals = useMemo(() => ({
    km: summaryRows.reduce((s, r) => s + Number(r.total_km || 0), 0),
    trips: summaryRows.reduce((s, r) => s + Number(r.trips || 0), 0),
    buses: summaryRows.length,
  }), [summaryRows]);

  // Create / tear down the map.
  useEffect(() => {
    if (!isOpen) return undefined;
    const id = setTimeout(() => {
      if (!containerRef.current || mapRef.current) return;
      const map = L.map(containerRef.current).setView(JOUNIEH, 12);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19, attribution: '&copy; OpenStreetMap contributors',
      }).addTo(map);
      layerRef.current = L.layerGroup().addTo(map);
      mapRef.current = map;
      setTimeout(() => map.invalidateSize(), 50);
    }, 0);
    return () => clearTimeout(id);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) return;
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; layerRef.current = null; }
  }, [isOpen]);

  // Draw LIVE markers.
  useEffect(() => {
    if (tab !== 'live') return;
    const map = mapRef.current, layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const latlngs = [];
    buses.forEach((b) => {
      if (b.latitude == null || b.longitude == null) return;
      const ll = [Number(b.latitude), Number(b.longitude)];
      latlngs.push(ll);
      const st = statusOf(b, overspeed);
      const spd = b.speedKmh != null ? `${Math.round(b.speedKmh)}km/h` : '';
      const icon = L.divIcon({
        className: '',
        html: `<div style="background:${COLORS[st]};color:#fff;border-radius:9999px;padding:2px 7px;font-size:11px;font-weight:700;white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,.45);border:2px solid #fff;transform:translate(-50%,-50%)">🚌#${b.busId}${spd ? ` · ${spd}` : ''}</div>`,
        iconSize: [0, 0],
      });
      const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));
      const popup = `<div style="font-size:13px;line-height:1.5"><b>Bus #${b.busId}</b>${st === 'speeding' ? ' · <span style="color:#dc2626;font-weight:700">SPEEDING</span>' : st === 'offline' ? ' · <span style="color:#6b7280">offline</span>' : ''}<br/>${b.driverName ? `Driver: ${esc(b.driverName)}<br/>` : ''}Speed: ${b.speedKmh != null ? Math.round(b.speedKmh) + ' km/h' : '—'}${b.sessionMaxSpeed != null ? ` (max ${Math.round(b.sessionMaxSpeed)})` : ''}<br/><span style="color:#555">Updated ${agoText(b.secondsAgo)}</span></div>`;
      L.marker(ll, { icon }).bindPopup(popup).addTo(layer);
    });
    if (latlngs.length && !fittedRef.current) { map.fitBounds(latlngs, { padding: [50, 50], maxZoom: 15 }); fittedRef.current = true; }
    setTimeout(() => map.invalidateSize(), 30);
  }, [buses, overspeed, tab]);

  // Draw HISTORY trajectory.
  useEffect(() => {
    if (tab !== 'history') return;
    const map = mapRef.current, layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const lls = routePoints.filter((p) => p.latitude != null && p.longitude != null)
      .map((p) => [Number(p.latitude), Number(p.longitude), p.speedKmh]);
    if (lls.length < 1) { setTimeout(() => map.invalidateSize(), 30); return; }
    for (let i = 1; i < lls.length; i++) {
      const sp = lls[i][2];
      const color = (sp != null && sp > overspeed) ? '#dc2626' : '#2563eb';
      L.polyline([[lls[i - 1][0], lls[i - 1][1]], [lls[i][0], lls[i][1]]], { color, weight: 4, opacity: 0.85 }).addTo(layer);
    }
    L.marker([lls[0][0], lls[0][1]], { icon: dotIcon('#16a34a', '▶') }).bindPopup('Start').addTo(layer);
    const end = lls[lls.length - 1];
    L.marker([end[0], end[1]], { icon: dotIcon('#111827', '🏁') }).bindPopup('End').addTo(layer);
    map.fitBounds(lls.map((p) => [p[0], p[1]]), { padding: [40, 40] });
    setTimeout(() => map.invalidateSize(), 30);
  }, [routePoints, overspeed, tab]);

  if (!isOpen) return null;

  const TabBtn = ({ id, icon, label }) => (
    <button
      onClick={() => { setTab(id); setSelected(null); }}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold ${tab === id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
    >{icon}{label}</button>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <div className="flex items-center gap-4">
            <Navigation className="w-6 h-6 text-indigo-600" />
            <h2 className="text-xl font-bold text-gray-900">Bus Control Center</h2>
            <div className="flex gap-2">
              <TabBtn id="live" icon={<Radio size={15} />} label="Live" />
              <TabBtn id="history" icon={<HistoryIcon size={15} />} label="History" />
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
        </div>

        {/* Sub-header controls */}
        {tab === 'live' ? (
          <div className="flex flex-wrap gap-3 items-center p-3 border-b bg-gray-50 text-sm">
            <span className="text-gray-500">{lastRefreshed ? `Updated ${lastRefreshed.toLocaleTimeString()}` : 'Loading…'} · auto 10s</span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-green-100 text-green-800 font-semibold">● {counts.active} active</span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-100 text-red-800 font-semibold"><AlertTriangle size={14} /> {counts.speeding} speeding</span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-200 text-gray-700 font-semibold"><WifiOff size={14} /> {counts.offline} offline</span>
            <label className="ml-auto inline-flex items-center gap-2 text-gray-700"><Gauge size={16} /> Overspeed &gt;
              <input type="number" value={overspeed} min={10} onChange={(e) => setOverspeed(Number(e.target.value) || 0)} className="w-20 px-2 py-1 border border-gray-300 rounded" /> km/h
            </label>
          </div>
        ) : (
          <div className="flex flex-wrap gap-3 items-center p-3 border-b bg-gray-50 text-sm">
            <label className="inline-flex items-center gap-2 text-gray-700">Last
              <select value={days} disabled={!!(from || to)} onChange={(e) => { setDays(Number(e.target.value)); setSelected(null); }} className="px-2 py-1 border border-gray-300 rounded disabled:opacity-50">
                <option value={1}>1 day</option><option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option>
              </select>
            </label>
            <label className="inline-flex items-center gap-1 text-gray-700">From
              <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setSelected(null); }} className="px-2 py-1 border border-gray-300 rounded" />
            </label>
            <label className="inline-flex items-center gap-1 text-gray-700">To
              <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setSelected(null); }} className="px-2 py-1 border border-gray-300 rounded" />
            </label>
            {(from || to) && (
              <button onClick={() => { setFrom(''); setTo(''); setSelected(null); }} className="text-xs text-blue-600 underline">clear dates</button>
            )}
            <select value={busFilter} onChange={(e) => setBusFilter(e.target.value)} className="px-2 py-1 border border-gray-300 rounded">
              <option value="all">All buses</option>
              {busOptions.map((b) => <option key={b} value={b}>Bus #{b}</option>)}
            </select>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-100 text-emerald-800 font-semibold">{totals.km.toFixed(1)} km</span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-100 text-blue-800 font-semibold">{totals.trips} trips</span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-100 text-amber-800 font-semibold">{totals.buses} buses</span>
            <label className="ml-auto inline-flex items-center gap-2 text-gray-700"><Gauge size={16} /> Speeding &gt;
              <input type="number" value={overspeed} min={10} onChange={(e) => setOverspeed(Number(e.target.value) || 0)} className="w-20 px-2 py-1 border border-gray-300 rounded" /> km/h
            </label>
          </div>
        )}

        <div className="flex flex-1 min-h-0">
          {/* Left list */}
          <div className="w-80 border-r overflow-y-auto">
            {tab === 'live' ? (
              <>
                {liveRows.length === 0 && !loading && (
                  <p className="p-4 text-sm text-gray-500">No buses have reported a position. Drivers start a route in the app to appear here.</p>
                )}
                {liveRows.map((b) => {
                  const st = statusOf(b, overspeed);
                  return (
                    <div key={b.busId} className={`p-3 border-b text-sm ${st === 'speeding' ? 'bg-red-50' : st === 'offline' ? 'bg-gray-50 opacity-70' : ''}`}>
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-gray-800">🚌 Bus #{b.busId}</span>
                        <span className="font-semibold" style={{ color: COLORS[st] }}>{b.speedKmh != null ? `${Math.round(b.speedKmh)} km/h` : '—'}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {b.driverName ? `${b.driverName} · ` : ''}{agoText(b.secondsAgo)}
                        {st === 'speeding' && <span className="text-red-600 font-semibold"> · SPEEDING</span>}
                        {st === 'offline' && <span className="text-gray-500"> · offline</span>}
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <>
                {summaryRows.length > 0 && (
                  <div className="p-3 border-b bg-gray-50">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Per bus ({days}d)</p>
                    <table className="w-full text-xs">
                      <thead className="text-gray-400">
                        <tr><th className="text-left font-medium">Bus</th><th className="text-right font-medium">Trips</th><th className="text-right font-medium">km</th><th className="text-right font-medium">Max</th><th className="text-right font-medium">Avg</th></tr>
                      </thead>
                      <tbody>
                        {summaryRows.map((r) => (
                          <tr key={r.bus_id} className="text-gray-700">
                            <td className="py-0.5 font-semibold">#{r.bus_id}</td>
                            <td className="py-0.5 text-right">{r.trips}</td>
                            <td className="py-0.5 text-right">{Number(r.total_km || 0).toFixed(0)}</td>
                            <td className="py-0.5 text-right">{Math.round(r.max_speed_kmh || 0)}</td>
                            <td className="py-0.5 text-right">{Math.round(r.avg_speed_kmh || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {sessionsLoading && <p className="p-4 text-sm text-gray-500">Loading route history…</p>}
                {!sessionsLoading && sessionRows.length === 0 && <p className="p-4 text-sm text-gray-500">No route sessions in this period.</p>}
                {sessionRows.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSelected(s)}
                    className={`w-full text-left p-3 border-b text-sm hover:bg-blue-50 ${selected?.id === s.id ? 'bg-blue-100' : ''}`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-800">🚌 Bus #{s.bus_id}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${s.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{s.status}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">{fmtDateTime(s.started_at)}</div>
                    <div className="text-xs text-gray-600 mt-1">
                      {Number(s.total_km || 0).toFixed(1)} km · {durationText(s.started_at, s.ended_at)} · max {Math.round(s.max_speed_kmh || 0)} km/h
                      {s.driver_name ? ` · ${s.driver_name}` : ''}
                    </div>
                  </button>
                ))}
              </>
            )}
          </div>

          {/* Map + (history) selected-session banner */}
          <div className="relative flex-1">
            <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />
            {tab === 'history' && selected && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[500] bg-white/95 border border-gray-200 rounded-lg shadow px-4 py-2 text-sm">
                <b>Bus #{selected.bus_id}</b> · {fmtDateTime(selected.started_at)} · {Number(selected.total_km || 0).toFixed(1)} km ·
                max {Math.round(selected.max_speed_kmh || 0)} / avg {Math.round(selected.avg_speed_kmh || 0)} km/h
                {routeLoading ? ' · loading…' : ` · ${routePoints.length} points`}
              </div>
            )}
            {tab === 'history' && !selected && (
              <div className="absolute inset-0 flex items-center justify-center text-gray-500 z-[400] pointer-events-none">Pick a route session on the left to see its trajectory.</div>
            )}
            {tab === 'live' && loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-[500]"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" /></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusLiveMapModal;
